import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { EventRole, UserContextRole, RolePermission } from '../types/enums/user-roles';
import { InternalRole } from '../types/enums/internal-roles.enum';
import { useAuth } from './AuthContext';
import { UserRole } from '../services/api/auth.api';

// Интерфейс контекста роли
interface RoleContextType {
  // Текущая активная роль (из AuthContext)
  currentRole: UserRole | null;
  
  // Все доступные роли пользователя (из AuthContext)
  availableRoles: UserRole[];
  
  // Контекст мероприятия (если применимо)
  currentEventId?: string;
  
  // Переключение роли
  switchRole: (roleId: string, eventId?: string) => Promise<void>;
  
  // Проверка разрешений
  hasPermission: (permission: string) => boolean;
  
  // Получение разрешений для роли
  getRolePermissions: (role: UserRole) => string[];
  
  // Проверка, является ли пользователь организатором
  isOrganizer: boolean;
  
  // Проверка, является ли пользователь поставщиком
  isSupplier: boolean;
  
  // Проверка, является ли пользователь участником
  isParticipant: boolean;
  
  // Проверка, является ли пользователь спикером
  isSpeaker: boolean;
  
  // Проверка внутренних ролей
  isInternalRole: (role: InternalRole) => boolean;
  
  // Проверка ролей событий
  isEventRole: (role: EventRole) => boolean;
  
  // Загрузка состояния
  isLoading: boolean;
  
  // Helper функции для обратной совместимости
  getCurrentRoleForComparison: () => UserContextRole | null;
  getAvailableRolesForComparison: () => UserContextRole[];
}

// Удалены mock данные - теперь используются данные из AuthContext

// Создаем контекст
const RoleContext = createContext<RoleContextType | undefined>(undefined);

// Провайдер контекста
interface RoleProviderProps {
  children: ReactNode;
  initialRole?: UserContextRole;
}

export const RoleProvider: React.FC<RoleProviderProps> = ({ 
  children, 
  initialRole: _initialRole = EventRole.ORGANIZER 
}) => {
  // Получаем данные из AuthContext
  const { 
    currentRole: authCurrentRole, 
    availableRoles: authAvailableRoles, 
    switchRole: authSwitchRole,
    hasPermission: authHasPermission,
    isInternalRole: authIsInternalRole,
    isEventRole: authIsEventRole,
    isLoading: authIsLoading 
  } = useAuth();
  
  // Локальное состояние
  const [currentEventId, setCurrentEventId] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  // Получение доступных ролей (теперь из AuthContext)
  const getAvailableRoles = useCallback(() => {
    return authAvailableRoles;
  }, [authAvailableRoles]);

  // Переключение роли (теперь через AuthContext)
  const switchRole = useCallback(async (roleId: string, eventId?: string) => {
    setIsLoading(true);
    try {
      await authSwitchRole(roleId, eventId);
      if (eventId) {
        setCurrentEventId(eventId);
      }
    } catch (error) {
      console.error('RoleContext: Ошибка переключения роли', error);
    } finally {
      setIsLoading(false);
    }
  }, [authSwitchRole]);

  // Проверка разрешений (теперь через AuthContext)
  const hasPermission = useCallback((permission: string): boolean => {
    return authHasPermission(permission);
  }, [authHasPermission]);

  // Получение разрешений для роли
  const getRolePermissions = useCallback((role: UserRole): string[] => {
    return role.permissions || [];
  }, []);

  // Вычисляемые свойства для удобства (теперь на основе AuthContext)
  // CRITICAL FIX: Добавлена проверка type === 'event' для предотвращения смешивания типов ролей
  const isOrganizer = authCurrentRole?.type === 'event' &&
                      (authCurrentRole?.role === EventRole.ORGANIZER || authCurrentRole?.role === EventRole.EVENT_ADMIN);
  const isSupplier = authCurrentRole?.type === 'event' && authCurrentRole?.role === EventRole.SUPPLIER;
  const isParticipant = authCurrentRole?.type === 'event' && authCurrentRole?.role === EventRole.PARTICIPANT;
  const isSpeaker = authCurrentRole?.type === 'event' && authCurrentRole?.role === EventRole.SPEAKER;

  // Helper функции для обратной совместимости
  const getCurrentRoleForComparison = useCallback((): UserContextRole | null => {
    if (!authCurrentRole) return null;
    
    // Преобразуем UserRole в UserContextRole для обратной совместимости
    if (authCurrentRole.type === 'internal') {
      return authCurrentRole.role as InternalRole;
    } else if (authCurrentRole.type === 'event') {
      return authCurrentRole.role as EventRole;
    }
    
    return null;
  }, [authCurrentRole]);

  const getAvailableRolesForComparison = useCallback((): UserContextRole[] => {
    return authAvailableRoles.map(role => {
      if (role.type === 'internal') {
        return role.role as InternalRole;
      } else if (role.type === 'event') {
        return role.role as EventRole;
      }
      return null;
    }).filter(Boolean) as UserContextRole[];
  }, [authAvailableRoles]);

  // Восстановление состояния из localStorage при загрузке (только для eventId)
  useEffect(() => {
    const savedEventId = localStorage.getItem('currentEventId');
    if (savedEventId) {
      setCurrentEventId(savedEventId);
    }
  }, []);

  // Значение контекста (exactOptionalPropertyTypes: не передаём currentEventId если undefined)
  const contextValue: RoleContextType = {
    currentRole: authCurrentRole,
    availableRoles: getAvailableRoles(),
    ...(currentEventId != null ? { currentEventId } : {}),
    switchRole,
    hasPermission,
    getRolePermissions,
    isOrganizer,
    isSupplier,
    isParticipant,
    isSpeaker,
    isInternalRole: authIsInternalRole,
    isEventRole: authIsEventRole,
    isLoading: isLoading || authIsLoading,
    // Helper функции для обратной совместимости
    getCurrentRoleForComparison,
    getAvailableRolesForComparison
  };

  return (
    <RoleContext.Provider value={contextValue}>
      {children}
    </RoleContext.Provider>
  );
};

// Хук для использования контекста
export const useRole = (): RoleContextType => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};

// Хук для проверки конкретной роли
export const useIsRole = (role: UserContextRole): boolean => {
  const { currentRole } = useRole();
  if (!currentRole) return false;
  
  // Преобразуем UserRole в UserContextRole для сравнения
  if (currentRole.type === 'internal') {
    return currentRole.role === role;
  } else if (currentRole.type === 'event') {
    return currentRole.role === role;
  }
  
  return false;
};

// Хук для проверки разрешения
export const useHasPermission = (permission: RolePermission): boolean => {
  const { hasPermission } = useRole();
  return hasPermission(permission);
};

// Хок для проверки множественных ролей
export const useHasAnyRole = (roles: UserContextRole[]): boolean => {
  const { currentRole } = useRole();
  if (!currentRole) return false;
  
  // Преобразуем UserRole в UserContextRole для сравнения
  const currentRoleForComparison = currentRole.type === 'internal' || currentRole.type === 'event' 
    ? currentRole.role 
    : null;
    
  return currentRoleForComparison ? roles.includes(currentRoleForComparison) : false;
}; 